import { useState } from "react";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { Crosshair, Grid3x3, Database, Target, ShieldCheck, LogOut, UsersRound, Download } from "lucide-react";
import raven from "@/assets/raven.png";
import { AuthProvider, useAuth } from "@/lib/auth";
import { TeamProvider, useTeam, colorFor } from "@/lib/team";
import AuthPage from "@/pages/AuthPage";
import ResolverTab from "@/pages/ResolverTab";
import MatrixTab from "@/pages/MatrixTab";
import FactionRatingsTab from "@/pages/FactionRatingsTab";
import EventImportTab from "@/pages/EventImportTab";
import HistoryTab from "@/pages/HistoryTab";
import AdminTab from "@/pages/AdminTab";
import TeamsManager from "@/pages/TeamsManager";

const BASE_TABS = [
  { id: "team", label: "My Team", icon: UsersRound, testid: "tab-team" },
  { id: "matrix", label: "Matchup Matrix", icon: Grid3x3, testid: "tab-matrix" },
  { id: "resolver", label: "Live Pairing", icon: Crosshair, testid: "tab-resolver" },
  { id: "history", label: "History", icon: Database, testid: "tab-history" },
  { id: "ratings", label: "Faction Ratings", icon: Target, testid: "tab-ratings" },
  { id: "teams", label: "Event Import", icon: Download, testid: "tab-teams" },
];

function PresenceDots({ presence }) {
  if (!presence?.length) return null;
  return (
    <div className="flex items-center -space-x-1" data-testid="presence-dots">
      {presence.slice(0, 6).map((u) => (
        <span key={u.id} title={u.username}
          className="h-6 w-6 rounded-full grid place-items-center text-[10px] font-bold border-2 border-[#0d1420]"
          style={{ background: colorFor(u.id), color: "#0b1020" }}>
          {u.username?.[0]?.toUpperCase()}
        </span>
      ))}
      <span className="text-[11px] text-slate-500 font-mono pl-2">{presence.length} online</span>
    </div>
  );
}

function Workspace() {
  const { user, logout } = useAuth();
  const { teams, activeId, setActiveId, activeTeam, setup, updateSetup, replaceSetup, presence } = useTeam();
  const [tab, setTab] = useState("team");

  const isAdmin = user?.role === "admin" || user?.role === "sub_admin";
  const TABS = isAdmin ? [...BASE_TABS, { id: "admin", label: "Admin", icon: ShieldCheck, testid: "tab-admin" }] : BASE_TABS;

  const header = (
    <header className="sticky top-0 z-30 glass border-b border-slate-800">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <img src={raven} alt="Spakr Hrafn raven" className="h-11 w-11 object-contain shrink-0" data-testid="app-logo" />
          <div>
            <h1 className="font-brand font-semibold tracking-wide text-2xl leading-none uppercase text-slate-100">Spakr Hrafn</h1>
            <p className="text-[11px] text-slate-500 font-mono tracking-wider mt-0.5">TEAM PAIRING RESOLVER</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {teams.length > 0 && (
            <select data-testid="team-switcher" value={activeId} onChange={(e) => setActiveId(e.target.value)}
              className="bg-slate-950/60 border border-slate-700 rounded-lg text-sm text-slate-200 px-3 py-2 max-w-[200px]">
              {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          )}
          <PresenceDots presence={presence} />
          <div className="hidden sm:block text-right">
            <p className="text-[10px] text-slate-500 font-mono uppercase tracking-wider">Signed in</p>
            <p className="text-sm font-semibold text-slate-200 truncate max-w-[140px]" data-testid="current-username">{user?.username}</p>
          </div>
          <button data-testid="logout-button" onClick={logout}
            className="flex items-center gap-1.5 text-xs font-semibold text-slate-400 hover:text-red-400 border border-slate-700 rounded-lg px-3 py-2 transition-colors">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </div>
      <nav className="max-w-[1400px] mx-auto px-2 sm:px-4 flex gap-1 overflow-x-auto">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button key={t.id} data-testid={t.testid} onClick={() => setTab(t.id)}
              className={`relative flex items-center gap-2 px-4 py-3 text-sm font-semibold whitespace-nowrap transition-colors ${
                active ? "text-amber-400" : "text-slate-400 hover:text-slate-200"}`}>
              <Icon className="h-4 w-4" />
              {t.label}
              {active && <span className="absolute left-2 right-2 bottom-0 h-0.5 bg-amber-400 rounded-full" />}
            </button>
          );
        })}
      </nav>
    </header>
  );

  const needsTeam = ["resolver", "matrix", "ratings", "history"].includes(tab);
  const gateMsg = !activeTeam
    ? { title: "No active team yet", body: "Create or join a team in the “My Team” tab to start pairing together." }
    : !setup
      ? { title: "Loading team workspace…", body: "" }
      : null;

  const content = () => {
    if (tab === "team") return <TeamsManager setup={setup} updateSetup={updateSetup} />;
    if (tab === "admin" && isAdmin) return <AdminTab />;
    if (tab === "teams") return <EventImportTab />;
    if (needsTeam && gateMsg) {
      return (
        <div className="min-h-[40vh] flex flex-col items-center justify-center text-center" data-testid="tab-gate-message">
          <p className="text-slate-200 font-semibold text-lg">{gateMsg.title}</p>
          {gateMsg.body && <p className="text-slate-500 text-sm mt-1 max-w-md">{gateMsg.body}</p>}
          {!activeTeam && (
            <button data-testid="goto-team-tab" onClick={() => setTab("team")}
              className="mt-4 bg-amber-500 text-slate-900 hover:bg-amber-400 font-semibold rounded-lg px-4 py-2">
              Go to My Team
            </button>
          )}
        </div>
      );
    }
    if (tab === "resolver") return <ResolverTab setup={setup} updateSetup={updateSetup} replaceSetup={replaceSetup} />;
    if (tab === "matrix") return <MatrixTab setup={setup} updateSetup={updateSetup} />;
    if (tab === "ratings") return <FactionRatingsTab setup={setup} updateSetup={updateSetup} />;
    if (tab === "history") return <HistoryTab />;
    return <TeamsManager />;
  };

  return (
    <div className="App min-h-screen text-slate-100">
      <div className="fixed inset-0 tac-grid opacity-40 pointer-events-none" />
      {header}
      <main className="relative z-10 max-w-[1400px] mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {content()}
      </main>
    </div>
  );
}

function Gate() {
  const { user } = useAuth();
  if (user === undefined) return <div className="min-h-screen flex items-center justify-center text-slate-400 font-mono text-sm">Loading…</div>;
  if (!user) return <AuthPage />;
  return (
    <TeamProvider>
      <Workspace />
    </TeamProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <Gate />
      <Toaster position="bottom-right" theme="dark" />
    </AuthProvider>
  );
}

export default App;
